import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import { InformationIcon, PharmacyIcon } from '@repo/ui/icons'
import StatusIndicator from '@repo/ui/status-indicator'
import TextLink from '@repo/ui/text-link'
import Typography from '@repo/ui/typography'
import { TFunction } from 'i18next'

import i18n from '../../../../i18n'
import { Paths } from '../../../lib/constants/path'
import { genderOneLetterMapping } from '../../../lib/types/enums/common/gender.enum'
import { schoolLevelEnumMapping } from '../../../lib/types/enums/common/school-level.enum'
import { EnrollmentPropositionTypeEnum } from '../../../lib/types/enums/enrollment/enrollment-proposition-type.enum'
import { MedicalConditionEnum } from '../../../lib/types/enums/enrollment/medical-condition-enum'
import { OtoIndividualTransferMethodEnum } from '../../../lib/types/enums/enrollment/oto-individual-transfer-method.enum'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { OtoIndividualPlacement } from '../../../lib/types/models/enrollment/oto-individual-enrollments'
import { EnrollmentDiffusionStateEnum } from '../../../lib/types/models/enrollment-center/enrollment-center'
import { PeriodTypes } from '../../../lib/types/models/period/period'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import InformationToolTip from '../../elements/information-tooltip'
import PeriodBadge from '../../elements/period-badge'
import TransportPicto from '../../elements/transport-picto'
import PlacementSummary from '../ols/centers/placement-summary'
import OtoIndividualHostFamilyCell from './oto-individual-host-family-cell'
import OtoIndividualPlacementStudentInfo from './oto-individual-placement-student-info'
import OtoIndividualTravelInfoCell from './oto-individual-travel-info-cell'

type ColumnType = Column<PeriodTypes.OtoIndividual> & {
  roles: RoleEnum[]
}

// the provider's acknowledge action, with the row it is currently running on
type OtoIndividualAcknowledgement = {
  onClick: (placement: OtoIndividualPlacement) => void
  isPending: boolean
  pendingId: number | null
}

type PlacementColumnType = Column<OtoIndividualPlacement> & {
  // Optional so columns without a role restriction are shown to all
  variant?: {
    role: RoleEnum
    order: number
  }[]
}

// A youth LEC removed from the periode: nothing may be recorded on it any more -- either the
// removal still has to be diffused, or the provider already sees it and only keeps the row to
// acknowledge it (the backend refuses every placement action on it either way).
export const isOtoIndividualPlacementRemoved = (placement: OtoIndividualPlacement) =>
  Boolean(placement.otoIndividualProposition.deleted) ||
  placement.otoIndividualProposition.diffusionState === EnrollmentDiffusionStateEnum.DELETED

const RemovedLabel = ({
  t,
  placement
}: {
  t: TFunction<AvailableLanguages, undefined>
  placement: OtoIndividualPlacement
}) => {
  if (placement.otoIndividualProposition.diffusionState !== EnrollmentDiffusionStateEnum.DELETED)
    return null

  return (
    <Typography variant="label" color={tailwindColors.red.DEFAULT}>
      {t('oto.individual.placements.removed')}
    </Typography>
  )
}

// grey when the period has no placement to judge, green/red otherwise
const renderAllHostFamilyInfoSentCell = (
  t: TFunction<AvailableLanguages, undefined>,
  allHostFamilyInfoSent: boolean,
  hasPlacements: boolean
) => (
  <InformationToolTip
    contentVariant="secondary"
    icon={
      <Badge
        text={
          <InformationIcon
            filled
            color={
              !hasPlacements
                ? tailwindColors.grey.DEFAULT
                : allHostFamilyInfoSent
                  ? tailwindColors.green.DEFAULT
                  : tailwindColors.red.DEFAULT
            }
          />
        }
        variant={!hasPlacements ? 'grey' : allHostFamilyInfoSent ? 'success' : 'danger'}
        className="h-8"
      />
    }
  >
    <Typography
      variant="label-medium"
      color={tailwindColors.black.DEFAULT}
      className="text-pretty"
    >
      {t(
        !hasPlacements
          ? 'oto.individual.periods.info.no.placements'
          : allHostFamilyInfoSent
            ? 'oto.individual.periods.info.all.sent'
            : 'oto.individual.periods.info.some.not.sent'
      )}
    </Typography>
  </InformationToolTip>
)

const otoIndividualsPlacePeriodColumns: (
  t: TFunction<AvailableLanguages, undefined>
) => ColumnType[] = (t) => [
  {
    accessorKey: 'nameEn',
    header: t('oto.individual.periods.column.period'),
    enableSorting: true,
    cell: ({ row }) => <PeriodBadge period={row.original} />,
    roles: [RoleEnum.ADMIN, RoleEnum.Ol]
  },
  {
    accessorKey: 'viewData.olPlacedEnrollmentsCount',
    header: t('oto.individual.periods.column.students.placed'),
    cell: ({ row }) => row.original.viewData?.olPlacedEnrollmentsCount ?? 0,
    roles: [RoleEnum.Ol]
  },
  {
    accessorKey: 'viewData.olUnplacedEnrollmentsCount',
    header: t('oto.individual.periods.column.students.unplaced'),
    cell: ({ row }) => row.original.viewData?.olUnplacedEnrollmentsCount ?? 0,
    roles: [RoleEnum.Ol]
  },
  {
    accessorKey: 'id',
    header: t('oto.individual.periods.column.students.affected'),
    roles: [RoleEnum.ADMIN],
    cell: ({ row }) => {
      const data = row.original.viewData

      if (!data) return null
      const totalStudents = data.placedEnrollmentsCount + data.unplacedEnrollmentsCount
      return (
        <PlacementSummary
          placed={data.placedEnrollmentsCount ?? 0}
          notPlaced={data.unplacedEnrollmentsCount ?? 0}
          total={totalStudents}
          className="flex-row"
        />
      )
    }
  },
  {
    accessorKey: 'id',
    header: t('oto.individual.periods.column.students.to.diffuse'),
    columnHeaderClassName: 'min-w-[200px] text-center',
    centerCell: true,
    roles: [RoleEnum.ADMIN],
    cell: ({ row }) => (
      <Badge
        text={`${row.original.viewData?.toDiffuseEnrollmentsCount ?? 0} / ${(row.original.viewData?.removedEnrollmentsCount ?? 0) * -1}`}
        variant="warning"
        size="flexible"
        className="px-2"
      />
    )
  },
  {
    accessorKey: 'id',
    header: t('oto.individual.periods.column.students.diffused'),
    centerCell: true,
    roles: [RoleEnum.ADMIN],
    cell: ({ row }) => {
      const { viewData } = row.original
      const enrollmentsDiffusedToOlAt = viewData?.enrollmentsDiffusedToOlAt
      return (
        <div className="flex flex-col gap-1 items-center py-2">
          <Badge
            text={
              (viewData?.olPlacedEnrollmentsCount ?? 0) +
              (viewData?.olUnplacedEnrollmentsCount ?? 0)
            }
          />
          {enrollmentsDiffusedToOlAt && (
            <Badge text={DateFormatter.DDMMYYYY(enrollmentsDiffusedToOlAt)} />
          )}
        </div>
      )
    }
  },
  {
    accessorKey: 'viewData.allHostFamilyInfoSent',
    header: t('oto.individual.periods.column.client.info.diffusion'),
    roles: [RoleEnum.ADMIN],
    cell: ({ row }) =>
      renderAllHostFamilyInfoSentCell(
        t,
        row.original.viewData?.allHostFamilyInfoSent ?? false,
        (row.original.viewData?.placedEnrollmentsCount ?? 0) > 0
      )
  },
  {
    accessorKey: 'viewData.olAllHostFamilyInfoSent',
    header: t('oto.individual.periods.column.client.info.diffusion'),
    roles: [RoleEnum.Ol],
    cell: ({ row }) =>
      renderAllHostFamilyInfoSentCell(
        t,
        row.original.viewData?.olAllHostFamilyInfoSent ?? false,
        (row.original.viewData?.olPlacedEnrollmentsCount ?? 0) > 0
      )
  }
]

export const getOtoIndividualPlacePeriodsColumns = (
  t: TFunction<AvailableLanguages, undefined>,
  Role: RoleEnum
): Column<PeriodTypes.OtoIndividual>[] => {
  const columns = otoIndividualsPlacePeriodColumns(t)
  return columns.filter((column) =>
    column.roles?.includes(Role)
  ) as Column<PeriodTypes.OtoIndividual>[]
}

// The placement row is the enrollment; its 1:1 OTO Individual proposition carries the stay, the
// placement and the diffusion (backend: enrollments/versions/enrollment-versions/oto-individual-list.ts).
const otoIndividualPlacementColumns: (
  t: TFunction<AvailableLanguages, undefined>,
  onHostFamilyClick?: (placement: OtoIndividualPlacement) => void,
  onTravelInfoClick?: (placement: OtoIndividualPlacement) => void,
  acknowledgement?: OtoIndividualAcknowledgement
) => PlacementColumnType[] = (t, onHostFamilyClick, onTravelInfoClick, acknowledgement) => [
  {
    accessorKey: 'otoIndividualProposition.hostFamilyId',
    header: t('oto.individual.placements.column.student.placed'),
    enableSorting: true,
    centerCell: true,
    variant: [{ role: RoleEnum.Ol, order: 1 }],
    cell: ({ row }) => (
      <StatusIndicator
        status={row.original.otoIndividualProposition.hostFamily ? 'active' : 'inactive'}
      />
    )
  },
  {
    accessorKey: 'otoIndividualProposition.hostFamilyDiffused',
    header: t('oto.individual.placements.column.parents.diffusion.status'),
    centerCell: true,
    variant: [{ role: RoleEnum.ADMIN, order: 1 }],
    cell: ({ row }) => (
      <StatusIndicator
        status={row.original.otoIndividualProposition.hostFamilyDiffused ? 'active' : 'inactive'}
      />
    )
  },
  {
    accessorKey: 'otoIndividualProposition.hostFamilyDiffusedAt',
    header: t('oto.individual.placements.column.last.parents.diffusion.date'),
    enableSorting: true,
    variant: [{ role: RoleEnum.ADMIN, order: 2 }],
    cell: ({ row }) =>
      row.original.otoIndividualProposition.hostFamilyDiffusedAt ? (
        <Badge
          text={DateFormatter.DDMMYYYY(row.original.otoIndividualProposition.hostFamilyDiffusedAt)}
        />
      ) : (
        '-'
      )
  },
  {
    accessorKey: 'otoIndividualProposition.hostFamily.parent1LastName',
    header: t('oto.individual.placements.column.host.family'),
    enableSorting: true,
    variant: [{ role: RoleEnum.ADMIN, order: 3 }],
    cell: ({ row }) => <OtoIndividualHostFamilyCell placement={row.original} withHistory />
  },
  {
    accessorKey: 'childLastName',
    header: t('oto.individual.placements.column.last.name'),
    enableSorting: true,
    variant: [{ role: RoleEnum.ADMIN, order: 4 }],
    cell: ({ row }) => (
      <div className="flex flex-col gap-1">
        <TextLink
          href={Paths.ENROLLMENT_PROFILE_PAGE.replace(':id', row.original.id.toString())}
          name={row.original.childLastName?.toUpperCase() || '-'}
        />
        <RemovedLabel t={t} placement={row.original} />
      </div>
    )
  },
  {
    accessorKey: 'childLastName',
    header: t('oto.individual.placements.column.last.name'),
    enableSorting: true,
    variant: [{ role: RoleEnum.Ol, order: 2 }],
    cell: ({ row }) => (
      <div className="flex flex-col gap-1">
        <Typography variant="secondary-body">
          {row.original.childLastName?.toUpperCase() || '-'}
        </Typography>
        <RemovedLabel t={t} placement={row.original} />
      </div>
    )
  },
  {
    accessorKey: 'childFirstName',
    header: t('oto.individual.placements.column.first.name'),
    enableSorting: true,
    variant: [
      { role: RoleEnum.Ol, order: 3 },
      { role: RoleEnum.ADMIN, order: 5 }
    ],
    cell: ({ row }) => <OtoIndividualPlacementStudentInfo placement={row.original} />
  },
  {
    accessorKey: 'otoIndividualProposition.departureDate',
    header: t('oto.individual.placements.column.dates.of.stay'),
    enableSorting: true,
    variant: [
      { role: RoleEnum.Ol, order: 4 },
      { role: RoleEnum.ADMIN, order: 10 }
    ],
    cell: ({ row }) => (
      <Badge
        className="h-fit py-2 text-nowrap"
        text={`${DateFormatter.DDMMYYYY(row.original.otoIndividualProposition.departureDate)} - ${DateFormatter.DDMMYYYY(row.original.otoIndividualProposition.returnDate)}`}
      />
    )
  },
  {
    accessorKey: 'otoIndividualProposition.numberOfWeeks',
    header: t('oto.individual.placements.column.weeks'),
    enableSorting: true,
    centerCell: true,
    variant: [
      { role: RoleEnum.Ol, order: 5 },
      { role: RoleEnum.ADMIN, order: 11 }
    ]
  },
  {
    accessorKey: 'childBirthDate',
    header: t('oto.individual.placements.column.age'),
    enableSorting: true,
    centerCell: true,
    variant: [
      { role: RoleEnum.Ol, order: 6 },
      { role: RoleEnum.ADMIN, order: 6 }
    ],
    cell: ({ row }) =>
      EnrollmentProperties.getAgeAtDeparture({
        ...row.original,
        propositionType: EnrollmentPropositionTypeEnum.OTO_INDIVIDUAL
      }) ?? '-'
  },
  {
    accessorKey: 'childSchoolLevel',
    header: t('oto.individual.placements.column.school.level'),
    enableSorting: true,
    centerCell: true,
    variant: [{ role: RoleEnum.ADMIN, order: 7 }],
    cell: ({ row }) =>
      valueRender(row.original.childSchoolLevel, (level) => schoolLevelEnumMapping[level])
  },
  {
    accessorKey: 'childGender',
    header: t('oto.individual.placements.column.sex'),
    enableSorting: true,
    centerCell: true,
    variant: [
      { role: RoleEnum.Ol, order: 7 },
      { role: RoleEnum.ADMIN, order: 8 }
    ],
    cell: ({ row }) =>
      valueRender(row.original.childGender, (gender) => genderOneLetterMapping[gender].label)
  },
  {
    accessorKey: 'medicalLevel',
    header: t('oto.individual.placements.column.health'),
    enableSorting: true,
    centerCell: true,
    variant: [
      { role: RoleEnum.Ol, order: 8 },
      { role: RoleEnum.ADMIN, order: 9 }
    ],
    cell: ({ row }) => {
      if (row.original.medicalLevel === MedicalConditionEnum.RAS) return '-'
      return (
        <PharmacyIcon
          className="!size-5 shrink-0"
          color={tailwindColors.green.DEFAULT}
          color2={tailwindColors.green.light.DEFAULT}
        />
      )
    }
  },
  {
    accessorKey: 'otoIndividualProposition.numberOfHours',
    header: t('oto.individual.placements.column.hours.per.week'),
    enableSorting: true,
    centerCell: true,
    variant: [
      { role: RoleEnum.Ol, order: 9 },
      { role: RoleEnum.ADMIN, order: 12 }
    ],
    cell: ({ row }) =>
      valueRender(row.original.otoIndividualProposition.numberOfHours, (hours) => `${hours}h`)
  },
  {
    accessorKey: 'otoIndividualProposition.option.title',
    header: t('oto.individual.placements.column.option'),
    enableSorting: true,
    centerCell: true,
    variant: [
      { role: RoleEnum.Ol, order: 10 },
      { role: RoleEnum.ADMIN, order: 13 }
    ],
    cell: ({ row }) => {
      const option = row.original.otoIndividualProposition.option
      if (!option) return '-'
      return (
        <InformationToolTip
          contentVariant="secondary"
          icon={
            option?.picto ? (
              <TransportPicto picto={option.picto} />
            ) : (
              <Badge icon={<InformationIcon filled color={tailwindColors.grey.DEFAULT} />} />
            )
          }
        >
          <Typography variant="label-medium" color={tailwindColors.black.DEFAULT}>
            {getTranslatedName(option, i18n.language) || '—'}
          </Typography>
        </InformationToolTip>
      )
    }
  },
  {
    accessorKey: 'otoIndividualProposition.arrivalPlace.title',
    header: t('oto.individual.placements.column.arrival.location'),
    enableSorting: true,
    centerCell: true,
    variant: [
      { role: RoleEnum.Ol, order: 11 },
      { role: RoleEnum.ADMIN, order: 14 }
    ],
    cell: ({ row }) => valueRender(row.original.otoIndividualProposition.arrivalPlace?.title)
  },
  {
    accessorKey: 'otoIndividualProposition.regionOfStay.place.name',
    header: t('oto.individual.placements.column.area.of.stay'),
    enableSorting: true,
    centerCell: true,
    variant: [
      { role: RoleEnum.Ol, order: 12 },
      { role: RoleEnum.ADMIN, order: 15 }
    ],
    cell: ({ row }) => valueRender(row.original.otoIndividualProposition.regionOfStay?.place?.name)
  },
  {
    accessorKey: 'otoIndividualProposition.transferMethod',
    header: t('oto.individual.placements.column.transfer.welcome'),
    enableSorting: true,
    centerCell: true,
    variant: [{ role: RoleEnum.ADMIN, order: 16 }],
    cell: ({ row }) =>
      row.original.otoIndividualProposition.transferMethod ===
      OtoIndividualTransferMethodEnum.AIRPORT_PICKUP
        ? t('oto.individual.placements.transfer.welcome.yes')
        : '-'
  },
  {
    accessorKey: 'otoIndividualProposition.hostFamily.familyName',
    header: t('oto.individual.placements.column.host.family'),
    enableSorting: true,
    variant: [{ role: RoleEnum.Ol, order: 13 }],
    cell: ({ row }) => (
      <OtoIndividualHostFamilyCell
        withHistory
        placement={row.original}
        // a removed youth may not be placed, unplaced or moved any more
        onActionClick={
          isOtoIndividualPlacementRemoved(row.original) ? undefined : onHostFamilyClick
        }
      />
    )
  },
  {
    accessorKey: 'otoIndividualProposition.hostFamilyDiffused',
    header: t('oto.individual.placements.column.hf.info.sent.to.parents'),
    enableSorting: true,
    centerCell: true,
    variant: [{ role: RoleEnum.Ol, order: 15 }],
    cell: ({ row }) => (
      <StatusIndicator
        status={row.original.otoIndividualProposition.hostFamilyDiffused ? 'active' : 'inactive'}
      />
    )
  },
  {
    accessorKey: 'travelInfo',
    header: t('oto.individual.placements.column.travel.info'),
    centerCell: true,
    variant: [
      { role: RoleEnum.Ol, order: 16 },
      { role: RoleEnum.ADMIN, order: 17 }
    ],
    cell: ({ row }) => (
      <OtoIndividualTravelInfoCell
        placement={row.original}
        // the accueil-transfert of a removed youth may not be edited any more
        onActionClick={
          isOtoIndividualPlacementRemoved(row.original) ? undefined : onTravelInfoClick
        }
      />
    )
  },
  {
    // the provider acknowledges that a youth LEC removed from the periode is gone
    accessorKey: 'id',
    header: '',
    variant: [{ role: RoleEnum.Ol, order: 14 }],
    cell: ({ row }) => {
      if (
        !acknowledgement ||
        row.original.otoIndividualProposition.diffusionState !==
          EnrollmentDiffusionStateEnum.DELETED
      )
        return null

      return (
        <Button
          className="border-red w-28 whitespace-normal break-words"
          variant="destructive"
          size="small"
          loading={acknowledgement.isPending && acknowledgement.pendingId === row.original.id}
          onClick={() => acknowledgement.onClick(row.original)}
        >
          {t('oto.individual.placements.acknowledgement')}
        </Button>
      )
    }
  },
  {
    accessorKey: 'otoIndividualProposition.diffusedToOlAt',
    header: t('oto.individual.placements.column.diffused.to.ol'),
    enableSorting: true,
    variant: [{ role: RoleEnum.ADMIN, order: 18 }],
    cell: ({ row }) =>
      row.original.otoIndividualProposition.diffusedToOlAt ? (
        <Badge
          text={DateFormatter.DDMMYYYY(row.original.otoIndividualProposition.diffusedToOlAt)}
        />
      ) : (
        '-'
      )
  }
]

export const getOtoIndividualPlacementColumns = (
  t: TFunction<AvailableLanguages, undefined>,
  Role: RoleEnum,
  onHostFamilyClick?: (placement: OtoIndividualPlacement) => void,
  onTravelInfoClick?: (placement: OtoIndividualPlacement) => void,
  acknowledgement?: OtoIndividualAcknowledgement
): Column<OtoIndividualPlacement>[] =>
  otoIndividualPlacementColumns(t, onHostFamilyClick, onTravelInfoClick, acknowledgement)
    .filter((column) => !column.variant || column.variant.some((v) => v.role === Role))
    .sort((a, b) => {
      const aOrder = a.variant?.find((v) => v.role === Role)?.order ?? 0
      const bOrder = b.variant?.find((v) => v.role === Role)?.order ?? 0
      return aOrder - bOrder
    })
